import { Alert, Badge, Button, buttonClass, cardClass, Table, textareaClass } from '@ledgerloom/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { hasFeature, isAdmin, useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Expandable, Icon, outcomeMessage, outcomeTone, schemaLabel, Time, useToast } from '../components/ui';
import { Field } from '../components/Field';
import { Stepper } from '../components/Stepper';
import { CLASS_HELP, type AgentRow } from './Agents';
import { AgentWorkNav } from './AgentWork';
import { inboxItem } from './Approvals';

// Build an agent in three steps: describe what it should do, review the draft
// the agent builder wrote, then have another admin sign it, run its eval check,
// and hire it in shadow. The wizard holds the build request in the URL, so a
// trip to the Inbox (to answer the builder's question) comes back to it.
// Patterns: advanced/wizard, user-feedback/progress-indicator,
// content-management/expandable-text for the procedure, data-display/table for
// the eval cases and check results, and forms/textarea for the request.

const STEPS = ['Describe', 'Review', 'Sign and check'];
const BUILDER_TEMPLATE = 'agent-builder';
const EXAMPLES = [
  'An agent that captures a lead from a short note I write after meeting someone',
  'An agent that drafts a thank-you note after every closed deal',
  'An agent that flags a quote whose discount is above our usual range',
];

interface Draft {
  playbook_id: string; title: string; description: string; class: string; principal_types: string[];
  triggers: Array<{ class: string; cron?: string; timezone?: string }>; scopes: string[]; allowed_schemas: string[];
  envelope?: Record<string, unknown>; stop_conditions?: Array<{ id: string; message: string }>; skill: string; reason: string;
  evals: Array<{ id: string; task: { kind: string; subject: Record<string, unknown> }; expect: { proposals?: Array<{ schema: string; payload_includes?: Record<string, unknown> }>; forbid?: string[] } }>;
}
interface BuildStatus {
  task: { id: string; status: 'queued' | 'claimed' | 'waiting' | 'done' | 'escalated' | 'cancelled'; outcome: { result?: string } | null; run_summary: string | null };
  proposal: { id: string; state: string; payload: Draft; rationale: string | null; approval_id: string | null; result: { playbook_id?: string; version?: number } | null } | null;
  question: { thread_id: string; status: string; question: string; exception_id: string | null } | null;
}
export interface Check {
  id: string; version: number; status: 'queued' | 'running' | 'passed' | 'failed'; cases: number | null; passed: number | null; error: string | null;
  results: Array<{ id: string; pass: boolean; failures: string[]; proposals?: Array<{ schema: string }> }> | null; created_at: string; finished_at: string | null;
}

export const PRINCIPAL_LABEL: Record<string, string> = { tenant: 'The whole company', team: 'A team', user: 'One person' };
const enc = encodeURIComponent;
const checksPath = (playbookId: string) => `/v1/playbooks/${enc(playbookId)}/checks`;

export function AgentBuild() {
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const taskId = params.get('task');
  const [status, setStatus] = useState<BuildStatus | null>(null);
  const [unmatched, setUnmatched] = useState(false);
  // Step 2 opens on the draft; step 3 once the person has read it.
  const [signing, setSigning] = useState(params.get('step') === 'sign');
  const [error, setError] = useState<unknown>(null);
  usePageTitle('Build an agent');
  const allowed = (isAdmin(me) || me.role === 'manager') && hasFeature(me, 'agent_builder');

  // The task's state, every two seconds while the builder is at work, is waiting
  // on a question, or its draft waits for a signature.
  useEffect(() => {
    if (!taskId) { setStatus(null); return; }
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const s = await api<BuildStatus>(`/v1/agents/build/${enc(taskId)}`);
        if (stop) return;
        setStatus(s);
        setError(null);
        if (!settled(s)) timer = setTimeout(tick, 2000);
      } catch (e) { if (!stop) setError(e); }
    };
    void tick();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [taskId]);

  const start = (task: string | null, step?: 'sign') => {
    const next = new URLSearchParams();
    if (task) next.set('task', task);
    if (step) next.set('step', step);
    setParams(next, { replace: !task });
    setSigning(step === 'sign');
    setUnmatched(false);
    setError(null);
  };
  const draft = status?.proposal ?? null;
  const step = draft && signing ? 2 : draft ? 1 : 0;

  if (!allowed) {
    return (
      <div className="stack">
        <AgentWorkNav />
        <div className="page-head"><div><h1>Build an agent</h1></div></div>
        <div className={cardClass}><Empty title="Building agents needs an admin or manager" action={<Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/agents">Back to hired agents</Link>}>Ask an admin to build it, or to turn the agent builder on for this workspace.</Empty></div>
      </div>
    );
  }
  return (
    <div className="stack" style={{ maxWidth: 920 }}>
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents', label: 'Agents' }, { label: 'Build an agent' }]} />
      <div className="page-head"><div><h1>Build an agent</h1><p>Say what the agent should do. The agent builder drafts its playbook, another admin signs it, a check proves it, and it starts in shadow.</p></div></div>
      <BuilderNotOn />
      <Stepper steps={STEPS} current={step} />
      <ErrorBanner error={error} />
      {step === 0 && (unmatched ? <NotRunning onRetry={() => setUnmatched(false)} /> : taskId
        ? <InProgress status={status} onStartOver={() => start(null)} />
        : <Describe initial={params.get('request') ?? ''} onQueued={(id) => start(id)} onUnmatched={() => setUnmatched(true)} />)}
      {step === 1 && draft && <Review draft={draft.payload} rationale={draft.rationale} onContinue={() => start(taskId, 'sign')} />}
      {step === 2 && draft && <SignAndCheck proposal={draft} onStartOver={() => start(null)} />}
    </div>
  );
}

// A draft the gate is still checking or that waits for its signatures; every
// other state is final.
const OPEN_STATES = new Set(['submitted', 'schema_valid', 'computed', 'rules_passed', 'pending_approval']);
const isOpen = (state: string) => OPEN_STATES.has(state);

// Polling stops once nothing more can arrive from the task: it ended without a
// draft, or its draft reached a final state.
function settled(s: BuildStatus): boolean {
  if (s.proposal) return !isOpen(s.proposal.state);
  return ['done', 'escalated', 'cancelled'].includes(s.task.status) && s.question?.status !== 'open';
}

// initial is a request drafted elsewhere (the setup walkthrough's wishes), for the person to read and send.
function Describe({ initial, onQueued, onUnmatched }: { initial: string; onQueued: (taskId: string) => void; onUnmatched: () => void }) {
  const [message, setMessage] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const short = message.trim().length < 10;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (short) { setError(new Error('Say a little more: at least ten characters.')); return; }
    setBusy(true); setError(null);
    try {
      const out = await api<{ outcome: string; task_id?: string }>('/v1/agents/build', { body: { message: message.trim() } });
      if (out.outcome === 'unmatched' || !out.task_id) onUnmatched(); else onQueued(out.task_id);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <form className={`${cardClass} card-pad stack`} onSubmit={submit} noValidate>
      <h2>What should the agent do?</h2>
      <ErrorBanner error={error} />
      <Field label="Your request" required hint="One or two sentences in your own words: what it watches, what it should produce, and for whom.">
        <textarea className={textareaClass} rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={EXAMPLES[0]} />
      </Field>
      <div className="small muted">
        <div className="faint">Requests that work well</div>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{EXAMPLES.map((x) => <li key={x}>{x}</li>)}</ul>
      </div>
      <div><Button type="submit" disabled={busy} aria-busy={busy}><Icon name="agent" />{busy ? 'Sending…' : 'Ask the builder'}</Button></div>
    </form>
  );
}

// Hired but not active, the builder drafts nothing that reaches a person: say so, and point at its page.
function BuilderNotOn() {
  const agents = useApi<AgentRow[]>('/v1/agents');
  const row = agents.data?.find((a) => a.template_id === BUILDER_TEMPLATE);
  if (!row || row.status === 'active') return null;
  return <Alert tone="info" role="status">The agent builder is not turned on yet, so its drafts reach no one. An admin turns it on from <Link to={`/agents/${enc(row.id)}`}>its page</Link>.</Alert>;
}

// No builder row is active or in shadow: say so, and point at its row.
function NotRunning({ onRetry }: { onRetry: () => void }) {
  const agents = useApi<AgentRow[]>('/v1/agents?include_retired=true');
  const row = agents.data?.find((a) => a.template_id === BUILDER_TEMPLATE);
  return (
    <div className={`${cardClass} card-pad stack`} role="status">
      <h2>The agent builder is not running</h2>
      <p style={{ margin: 0 }}>Nothing took your request: the agent builder is not hired, or is paused. Resume it on Hired agents, or hire it, then ask again.</p>
      <div className="row">
        {row ? <Link className={buttonClass({ variant: 'secondary' })} to={`/agents/${enc(row.id)}`}>Open the agent builder</Link> : <Link className={buttonClass({ variant: 'secondary' })} to="/agents/hire?template=agent-builder">Hire the agent builder</Link>}
        <Button type="button" variant="ghost" onClick={onRetry}>Ask again</Button>
      </div>
    </div>
  );
}

// The builder at work: queued, running, waiting on a question, or finished with
// nothing to draft.
function InProgress({ status, onStartOver }: { status: BuildStatus | null; onStartOver: () => void }) {
  const head = useRef<HTMLHeadingElement>(null);
  const q = status?.question;
  const finished = status && ['done', 'escalated', 'cancelled'].includes(status.task.status) && q?.status !== 'open';
  useEffect(() => { head.current?.focus(); }, []);
  if (finished) {
    return (
      <div className={`${cardClass} card-pad stack`}>
        <h2 ref={head} tabIndex={-1}>The builder finished without a draft</h2>
        <div className="faint">From the agent</div>
        <div className="note">{status.task.run_summary ?? `The request ended as ${status.task.outcome?.result ?? status.task.status}.`}</div>
        <div><Button type="button" variant="secondary" onClick={onStartOver}>Start over</Button></div>
      </div>
    );
  }
  return (
    <div className={`${cardClass} card-pad stack`} aria-live="polite">
      <h2 ref={head} tabIndex={-1}>{q && q.status === 'open' ? 'The builder has a question' : 'The builder is drafting'}</h2>
      {q && q.status === 'open' ? (
        <>
          <div className="faint">From the agent</div>
          <div className="note">{q.question}</div>
          <p className="small muted" style={{ margin: 0 }}>Answer it under Needs you. The builder goes on with your answer, and this page picks the draft up when it lands.</p>
          <div>{q.exception_id ? <Link className={buttonClass()} to={inboxItem(`exception:${q.exception_id}`)}>Answer it under Needs you</Link> : <Link className={buttonClass()} to="/desk/needs-you">See what needs you</Link>}</div>
        </>
      ) : (
        <div className="row" role="status"><span className="spinner" aria-hidden="true" /><span>{status ? `Reading what exists and writing the playbook (${status.task.status})` : 'Sending your request'}…</span></div>
      )}
    </div>
  );
}

// The draft, section by section, as the signer will see it.
function Review({ draft: d, rationale, onContinue }: { draft: Draft; rationale: string | null; onContinue: () => void }) {
  const head = useRef<HTMLHeadingElement>(null);
  useEffect(() => { head.current?.focus(); }, []);
  const envelope = Object.entries(d.envelope ?? {});
  return (
    <div className={`${cardClass} card-pad stack`} style={{ gap: 14 }}>
      <h2 ref={head} tabIndex={-1}>Review the draft</h2>
      <div>
        <div className="row"><strong style={{ fontSize: 18 }}>{d.title}</strong><Badge tone="accent" style={{ textTransform: 'capitalize' }}>{d.class}</Badge></div>
        <p style={{ margin: '4px 0 0' }}>{d.description}</p>
        <p className="small muted" style={{ margin: '4px 0 0' }}>{CLASS_HELP[d.class]}</p>
      </div>
      <dl className="facts">
        <dt>Acts for</dt><dd>{d.principal_types.map((p) => PRINCIPAL_LABEL[p] ?? p).join(', ')}</dd>
        <dt>Runs on</dt><dd>{d.triggers.map((t) => t.class + (t.cron ? ` (${t.cron}${t.timezone ? ` ${t.timezone}` : ''})` : '')).join(', ')}</dd>
        <dt>May read</dt><dd>{d.scopes.length ? d.scopes.join(', ') : 'Nothing beyond the task'}</dd>
        <dt>May propose</dt><dd>{d.allowed_schemas.length ? d.allowed_schemas.map(schemaLabel).join(', ') : 'Nothing (read-only)'}</dd>
        <dt>Limits</dt><dd>{envelope.length ? envelope.map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}`).join(' · ') : 'The class defaults'}</dd>
        {d.stop_conditions?.length ? <><dt>Stops when</dt><dd>{d.stop_conditions.map((s) => s.message).join(' ')}</dd></> : null}
        <dt>Playbook id</dt><dd className="mono">{d.playbook_id}</dd>
      </dl>
      {rationale && <div className="stack" style={{ gap: 4 }}><div className="faint">Builder's reason</div><div className="note">{rationale}</div></div>}
      <section className="stack" style={{ gap: 6 }} aria-labelledby="skill-h">
        <h3 id="skill-h" style={{ margin: 0 }}>The procedure</h3>
        <p className="small muted" style={{ margin: 0 }}>What the agent follows, word for word. It runs only at the version an admin signs.</p>
        <Expandable label={['Show the procedure', 'Hide the procedure']} defaultOpen><pre className="json note" aria-label="Procedure text">{d.skill}</pre></Expandable>
      </section>
      <section className="stack" style={{ gap: 6 }} aria-labelledby="evals-h">
        <h3 id="evals-h" style={{ margin: 0 }}>Eval cases ({d.evals.length})</h3>
        <p className="small muted" style={{ margin: 0 }}>The check runs each one through a hidden row before anything can be hired.</p>
        <Table label="Eval cases">
          <caption className="sr-only">Eval cases the check runs</caption>
          <thead><tr><th scope="col">Case</th><th scope="col">Task</th><th scope="col">Expected</th></tr></thead>
          <tbody>{d.evals.map((c) => <tr key={c.id}><td className="mono small">{c.id}</td><td className="small">{c.task.kind}</td><td className="small">{expected(c.expect)}</td></tr>)}</tbody>
        </Table>
      </section>
      <div><Button type="button" onClick={onContinue}>Continue to sign and check</Button></div>
    </div>
  );
}

function expected(e: Draft['evals'][number]['expect']): string {
  const parts: string[] = [];
  if (e.proposals?.length) parts.push(`proposes ${e.proposals.map((p) => schemaLabel(p.schema)).join(', ')}`);
  else parts.push('proposes nothing');
  if (e.forbid?.length) parts.push(`never ${e.forbid.map(schemaLabel).join(', ')}`);
  return parts.join('; ');
}

// Another admin signs the draft under Needs you; then the check runs; then the hire.
function SignAndCheck({ proposal, onStartOver }: { proposal: NonNullable<BuildStatus['proposal']>; onStartOver: () => void }) {
  const { me, refreshStats } = useSession();
  const toast = useToast();
  const head = useRef<HTMLHeadingElement>(null);
  const d = proposal.payload;
  const signed = proposal.state === 'applied';
  const playbookId = proposal.result?.playbook_id ?? d.playbook_id;
  const version = proposal.result?.version ?? 1;
  const checks = useApi<Check[]>(signed ? checksPath(playbookId) : null, [signed, playbookId]);
  const latest = checks.data?.find((c) => c.version === version) ?? null;
  const checking = latest && ['queued', 'running'].includes(latest.status);
  const [busy, setBusy] = useState<'check' | 'hire' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [hire, setHire] = useState<Outcome | null>(null);
  const [before, setBefore] = useState<string[] | null>(null);
  const [hired, setHired] = useState<AgentRow | null>(null);
  useEffect(() => { head.current?.focus(); }, []);
  // While a check runs, every two seconds; after a hire, until the signed row appears.
  const reloadChecks = checks.reload;
  useEffect(() => { if (!checking) return; const t = setInterval(() => void reloadChecks(), 2000); return () => clearInterval(t); }, [checking, reloadChecks]);
  useEffect(() => {
    if (!hire || hired || !before) return;
    const t = setInterval(async () => {
      const rows = await api<AgentRow[]>('/v1/agents').catch(() => null);
      const row = rows?.find((a) => a.template_id === playbookId && !before.includes(a.id));
      if (row) setHired(row);
    }, 2000);
    return () => clearInterval(t);
  }, [hire, hired, before, playbookId]);

  async function runCheck() {
    setBusy('check'); setError(null);
    try { await api(`/v1/playbooks/${enc(playbookId)}/versions/${version}/check`, { body: {} }); await checks.reload(); } catch (e) { setError(e); } finally { setBusy(null); }
  }
  async function hireInShadow() {
    setBusy('hire'); setError(null);
    try {
      const rows = await api<AgentRow[]>('/v1/agents');
      setBefore(rows.map((a) => a.id));
      const forCompany = d.principal_types.includes('tenant');
      const out = await api<Outcome>('/v1/agents/hire', { body: { template_id: playbookId, ...(forCompany ? { principal_type: 'tenant' } : { principal_type: 'user', principal_id: me.user_id }), reason: `Hire ${d.title}, built here` } });
      setHire(out);
      toast(outcomeMessage(out, 'Hired in shadow'), outcomeTone(out));
      refreshStats();
    } catch (e) { setError(e); } finally { setBusy(null); }
  }

  return (
    <div className={`${cardClass} card-pad stack`} style={{ gap: 14 }}>
      <h2 ref={head} tabIndex={-1}>Sign and check</h2>
      <ol className="stack small" style={{ margin: 0, paddingLeft: 20, gap: 10 }}>
        <li>
          <strong>Admins sign the draft.</strong> It waits under Needs you as a change set the builder wrote: two admins sign it when the workspace has two, and signing it signs the playbook. The item says how many signatures it still needs.
          <div className="row" style={{ marginTop: 6 }}>
            {signed ? <Badge tone="success">Signed as v{version}</Badge>
              : isOpen(proposal.state) ? <><Badge tone="warning">Waiting for a signature</Badge>{proposal.approval_id && <Link to={inboxItem(`approval:${proposal.approval_id}`)}>Open the approval under Needs you</Link>}</>
              : <><Badge tone="danger">{proposal.state === 'rejected' ? 'Rejected' : proposal.state}</Badge><Button type="button" variant="ghost" size="sm" onClick={onStartOver}>Start over</Button></>}
          </div>
        </li>
        <li>
          <strong>The check runs its eval cases.</strong> The runner puts the signed text through a hidden test copy of the agent, one task per case, and scores what it proposed. Nothing is written.
          {signed && (
            <div className="stack" style={{ marginTop: 6, gap: 8 }}>
              {!latest && <div><Button type="button" disabled={busy === 'check'} aria-busy={busy === 'check'} onClick={runCheck}>{busy === 'check' ? 'Queueing…' : 'Run the eval check'}</Button></div>}
              {latest && <CheckResult check={latest} />}
              {latest?.status === 'failed' && <div><Button type="button" variant="secondary" disabled={busy === 'check'} onClick={runCheck}>Run it again</Button></div>}
            </div>
          )}
        </li>
        <li>
          <strong>Hire it in shadow.</strong> Once the check passed, the hire is a change set another admin signs. In shadow the agent proposes and is logged, and nothing is written.
          {latest?.status === 'passed' && !hire && <div style={{ marginTop: 6 }}><Button type="button" disabled={busy === 'hire'} aria-busy={busy === 'hire'} onClick={hireInShadow}>{busy === 'hire' ? 'Sending…' : 'Hire in shadow'}</Button></div>}
          {hire && (
            <div className="stack" style={{ marginTop: 6, gap: 6 }} role="status">
              {hired ? <div className="row"><Badge tone="success">Hired in shadow</Badge><Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={`/agents/${enc(hired.id)}`}>Open {hired.name}</Link></div>
                : hire.state === 'pending_approval' ? <div className="row"><span className="spinner" aria-hidden="true" /><span>Hire proposed. Once another admin signs it, the agent appears here and on <Link to="/agents">Hired agents</Link>.</span></div>
                : <Badge tone={outcomeTone(hire) === 'error' ? 'danger' : 'success'}>{outcomeMessage(hire, 'Hired in shadow')}</Badge>}
            </div>
          )}
        </li>
      </ol>
      <ErrorBanner error={error} />
    </div>
  );
}

// One check: a progress indicator while it runs, then the per-case results.
export function CheckResult({ check: c }: { check: Check }) {
  if (c.status === 'queued' || c.status === 'running') {
    return <div className="row" role="status" aria-live="polite"><span className="spinner" aria-hidden="true" /><span>Checking {c.cases ?? ''} case{c.cases === 1 ? '' : 's'} ({c.status})…</span></div>;
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row" role="status">
        <Badge tone={c.status === 'passed' ? 'success' : 'danger'}>{c.status === 'passed' ? 'Passed' : 'Failed'}</Badge>
        <span className="small">{c.passed ?? 0} of {c.cases ?? 0} cases passed{c.finished_at && <> · <Time iso={c.finished_at} /></>}</span>
      </div>
      {c.error && <Alert role="note" tone="danger">{c.error}</Alert>}
      {c.results?.length ? (
        <Table label="Check results">
          <caption className="sr-only">Result per eval case</caption>
          <thead><tr><th scope="col">Case</th><th scope="col">Result</th><th scope="col">Proposed</th><th scope="col">Notes</th></tr></thead>
          <tbody>{c.results.map((r) => <tr key={r.id}><td className="mono small">{r.id}</td><td><Badge tone={r.pass ? 'success' : 'danger'}>{r.pass ? 'Pass' : 'Fail'}</Badge></td><td className="small">{r.proposals?.length ? r.proposals.map((p) => schemaLabel(p.schema)).join(', ') : 'nothing'}</td><td className="small">{r.failures.join('; ')}</td></tr>)}</tbody>
        </Table>
      ) : null}
    </div>
  );
}
